import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { Tooltip } from './Tooltip';

type Tone = 'accent' | 'soft' | 'danger' | 'outline' | 'ghost';

const tones: Record<Tone, string> = {
  accent: 'accent-aurora text-white',
  soft: 'bg-accent-soft text-accent-strong ring-1 ring-accent/10',
  danger: 'bg-danger-soft text-danger ring-1 ring-danger/10',
  outline: 'border border-hairline bg-surface text-ink',
  ghost: 'text-muted hover:bg-surface-2/70',
};

/** Icon-only action button with a tooltip label. Primary interactive pattern for
 *  row-level and secondary actions throughout the app. */
export const IconAction = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    tone?: Tone;
    size?: 'sm' | 'md';
    tooltipSide?: 'top' | 'bottom';
  }
>(function IconAction(
  {
    label,
    tone = 'soft',
    size = 'md',
    tooltipSide = 'top',
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const btn = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-none press disabled:opacity-40 disabled:pointer-events-none',
        size === 'sm' ? 'size-9' : 'size-10',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
  return (
    <Tooltip label={label} side={tooltipSide}>
      {btn}
    </Tooltip>
  );
});
